/**
 * recordingService.js
 *
 * Handles interview video upload via the FastAPI backend, which stores the
 * recording in Cloudflare R2.
 *
 * Firebase Storage is no longer used for video uploads.
 * Firebase Auth and Firestore are unchanged.
 */

import { getAuthToken } from "../api";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

/**
 * Upload an interview recording blob to the backend.
 *
 * The backend (POST /invite/interviews/{interviewId}/recording) uploads the
 * file to Cloudflare R2 and persists metadata to Firestore, then returns a
 * presigned R2 URL as `recordingUrl` — the same shape as the old Firebase
 * Storage response, so the rest of the frontend is unaffected.
 *
 * @param {Object} params
 * @param {Blob}   params.blob        - The recorded video blob.
 * @param {string} params.jobId       - Job / campaign ID.
 * @param {string} params.interviewId - Invite interview ID.
 * @param {number} [params.duration]  - Recording duration in seconds.
 * @param {Function} [params.onProgress] - Called with a 0-100 number.
 *
 * @returns {Promise<{recordingUrl: string, duration: number, storagePath: string}>}
 */
export function uploadInterviewRecording({
  blob,
  jobId,
  interviewId,
  duration,
  onProgress
}) {
  if (!blob || !jobId || !interviewId) {
    return Promise.reject(new Error("Recording upload is missing required data"));
  }

  return uploadRecordingViaBackend({ blob, jobId, interviewId, duration, onProgress });
}

/**
 * POST the recording blob to the FastAPI backend as multipart/form-data.
 *
 * The backend uploads the file to Cloudflare R2, writes Firestore metadata,
 * and returns { recordingUrl, duration, storagePath }.
 */
async function uploadRecordingViaBackend({ blob, jobId, interviewId, duration, onProgress }) {
  const token = await getAuthToken();

  const formData = new FormData();
  formData.append("recording", blob, `${interviewId}.webm`);
  formData.append("jobId", jobId);
  formData.append("duration", String(duration || 0));

  // Signal indeterminate progress while waiting for the server round-trip.
  if (onProgress) onProgress(10);

  const response = await fetch(
    `${API_BASE}/invite/interviews/${interviewId}/recording`,
    {
      method: "POST",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: formData
    }
  );

  if (onProgress) onProgress(100);

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || "Unable to upload recording");
  }

  return {
    recordingUrl: data.recordingUrl,
    duration:     data.duration ?? duration,
    storagePath:  data.storagePath
  };
}
